import { useQuery } from '@tanstack/react-query';
import { chunk, isEmpty, uniq, xor } from 'lodash';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Snackbar } from 'src/components/snackbar/Snackbar';
import { COLLECTIONS, USER_ACTIONS } from 'src/constants/constants';
import { PATHS } from 'src/constants/paths';
import { useFetchAssembliesWithVariations } from 'src/features/assemblies/bulkReplaceReviewPage/hooks/AssemblyWithVariation.hook';
import {
  BULK_REPLACE_COMPONENT_INCLUDES,
  useBulkReplaceAssemblyComponents,
  useGetAllAssemblyComponentsByAssemblyIds,
  useGetComponentsWithIncludes,
  useModal,
  useParamQuery,
} from 'src/hooks';
import { useBulkReplaceStageAssemblyComponents } from 'src/hooks/api/assembly/useBulkReplaceStageAssemblyComponents';
import { ASSEMBLY_ID_CHUNK_SIZE } from 'src/hooks/api/assembly/useGetAllAssemblyComponents';
import { fetchAssembliesWithoutPagination } from 'src/hooks/api/assembly/useGetAssemblies';
import { useGetBulkReplaceEligibleAssemblyId } from 'src/hooks/api/domainSpecific/bulkReplace/useGetBulkReplaceEligibleAssemblyId';
import { useGetBulkReplaceRequestNamesByAssemblyIds } from 'src/hooks/api/domainSpecific/bulkReplace/useGetBulkReplaceRequestNamesByAssemblyIds';
import { fetchDefaultAssemblies } from 'src/hooks/api/schedules/scheduleAssembly/useGetDefaultAssembly';
import { fetchScheduleDayparts } from 'src/hooks/api/schedules/scheduleDaypart/useGetScheduleDaypart';
import { narrativeGenerators } from 'src/hooks/api/userActions/helper';
import { IDefaultAssembly } from 'src/interfaces';
import { lang, queryKeys } from 'src/utils';

import {
  getChildInheritedIds,
  getSelectedAssembiles,
} from '../BulkReplaceReviewPage.helper';
import { groupAssembliesbyLevel } from '../modules/bulkReplaceSummaryPage/BulkReplaceSummaryPage.helper';

export const useBulkReplaceReviewPage = () => {
  const navigate = useNavigate();
  const publicationModal = useModal();

  const { activeParams } = useParamQuery();

  const { assemblies = [], c1 = '', c2 = '' } = activeParams;
  const assembliesIds = assemblies.map((e: string) => parseInt(e));

  if (assemblies.length === 0 || c1.length === 0 || c2.length === 0) {
    navigate({
      pathname: PATHS.NOT_FOUND,
    });
  }

  const componentsIds = [c1.toString(), c2.toString()];
  const { data: componentData } = useGetComponentsWithIncludes(
    [`id.list=${componentsIds.join('|')}`],
    BULK_REPLACE_COMPONENT_INCLUDES,
  );

  const selectedComponent =
    componentData &&
    componentData.find((item) => c1.includes(item.id.toString()));
  const replaceComponent =
    componentData &&
    componentData.find((item) => c2.includes(item.id.toString()));

  const { data: stageAssemblyData = [] } =
    useGetBulkReplaceRequestNamesByAssemblyIds(
      {
        assembly_ids: assembliesIds,
        from_component_id: selectedComponent?.id,
        statuses: ['staged'],
      },
      assembliesIds.length > 0 && selectedComponent?.id !== undefined,
    );

  const [selectedAssemblyIds, setSelectedAssemblyIds] =
    useState<number[]>(assembliesIds);
  const [expendAssemblies, setExpendAssemblies] = useState<number[]>([]);
  const [saveModalOpen, setSaveModalOpen] = useState<boolean>(false);
  const [discardModalOpen, setDiscardModalOpen] = useState<boolean>(false);
  const [displaySummaryPage, setDisplaySummaryPage] = useState<boolean>(false);
  const [updatedAssemblyComponents, setUpdatedAssemblyComponents] = useState<
    number[]
  >([]);
  const [stageBulkReplaceNameModal, setStageBulkReplaceNameModal] =
    useState<boolean>(false);
  const [stageBulkDisplayName, setStageBulkDisplayName] = useState('');
  const {
    data: bulkReplaceEligibleAssemblyIdData,
    isLoading: isLoadingBulkReplaceEligibleAssemblyIdData,
  } = useGetBulkReplaceEligibleAssemblyId(
    [`component_id=${selectedComponent?.id}`],
    selectedComponent?.id !== undefined,
  );

  let allowedAssemblyIds: number[] =
    bulkReplaceEligibleAssemblyIdData?.assembly_id ?? [];

  const { allAssemblyIds } = useFetchAssembliesWithVariations(assembliesIds);

  const {
    data: assemblyComponents = [],
    isLoading: isLoadingAssemblyComponents,
  } = useGetAllAssemblyComponentsByAssemblyIds(
    allAssemblyIds,
    [`component_id=${selectedComponent?.id}`],
    {
      enabled: allAssemblyIds.length > 0 && selectedComponent?.id !== undefined,
    },
  );

  const bulkReplaceAssembly = useBulkReplaceAssemblyComponents();

  const { mutateAsync, isLoading } = useBulkReplaceStageAssemblyComponents();

  const onSelectedAssemblyIds = (checked: boolean, id: number) => {
    if (checked) {
      setSelectedAssemblyIds([...selectedAssemblyIds, id]);
    } else {
      const uncheckedList = selectedAssemblyIds.filter(
        (assembly) => assembly !== id,
      );
      setSelectedAssemblyIds([...uncheckedList]);
    }
  };

  const onExpendedAssemblies = (id: number) => {
    setExpendAssemblies(xor(expendAssemblies, [id]));
  };

  const handleOpenSummaryPage = () => {
    setDisplaySummaryPage(true);

    /* TODO: Remove the check for individual locationCodes in Slice 1 */
    if (!isEmpty(locationGroupIdsFromResponse)) {
      publicationModal.onOpenModal();
    }
  };

  const handleCloseSummaryPage = () => {
    setUpdatedAssemblyComponents([]);
    setDisplaySummaryPage(false);
    navigate({
      pathname: PATHS.ASSEMBLIES,
    });
  };

  const onCloseSummaryPage = () => {
    handleCloseSummaryPage();
  };

  // Chunked to avoid 8KB URL length overflow at ~1100+ IDs (see CC-19915).
  const sortedAllAssemblyIds = [...allAssemblyIds].sort((a, b) => a - b);
  const { data: assembly, isLoading: isLoadingAssembly } = useQuery({
    queryKey: queryKeys.assemblies.all([
      `id.list=${sortedAllAssemblyIds.join('|')}`,
      'pageSize=0',
    ]),
    queryFn: async () => {
      const idChunks = chunk(sortedAllAssemblyIds, ASSEMBLY_ID_CHUNK_SIZE);
      const responses = await Promise.all(
        idChunks.map((c) =>
          fetchAssembliesWithoutPagination([`id.list=${c.join('|')}`]),
        ),
      );

      return responses.flat();
    },
    enabled: !isEmpty(allAssemblyIds),
    staleTime: Infinity,
  });

  const allAssemblies = assembly?.flatMap((page) => page);

  const assembliesListMapped = allAssemblies?.map((item) => {
    return {
      ...item,
      assemblyComponents: assemblyComponents?.filter(
        (component) => component.assembly_id === item.id,
      ),
    };
  });

  const response = getSelectedAssembiles(assembliesIds, assembliesListMapped);

  const assembliesList = groupAssembliesbyLevel(
    response && response.sort((a, b) => b.id - a.id),
    assembliesListMapped,
  );

  const assemblyListForRowsIds =
    assembliesList?.flatMap((item) =>
      uniq([item.id, ...item.childrenAssemblies.map((child) => child.id)]),
    ) ?? [];

  // Chunked to avoid 8KB URL length overflow at ~1100+ IDs (see CC-19915).
  const sortedAssemblyListForRowsIds = [...(assemblyListForRowsIds ?? [])].sort(
    (a, b) => a - b,
  );
  const { data: defaultAssemblies = [] } = useQuery<IDefaultAssembly[]>({
    queryKey: queryKeys.defaultAssemblies.all([
      `assembly_id.list=${sortedAssemblyListForRowsIds.join('|')}`,
      'timing',
    ]),
    queryFn: async () => {
      const idChunks = chunk(
        sortedAssemblyListForRowsIds,
        ASSEMBLY_ID_CHUNK_SIZE,
      );
      const responses = await Promise.all(
        idChunks.map((c) =>
          fetchDefaultAssemblies([`assembly_id.list=${c.join('|')}`], 'timing'),
        ),
      );

      return responses.flat();
    },
    enabled: !isEmpty(assemblyListForRowsIds),
  });

  const locationGroupIdsFromResponse = defaultAssemblies.map(
    (assembly) => assembly.location_group_id,
  );

  const uniqueLocationGroupIds = uniq(locationGroupIdsFromResponse).sort(
    (a, b) => a - b,
  );

  // Chunked to avoid 431 — a single unbounded location_group_id.list= with
  // 1000+ assemblies exceeds the 8KB header limit (see CC-22861).
  const { data: locationDayParts } = useQuery({
    queryKey: queryKeys.scheduleDayparts.all([
      `location_group_id.list=${uniqueLocationGroupIds.join('|')}`,
    ]),
    queryFn: async () => {
      const idChunks = chunk(uniqueLocationGroupIds, ASSEMBLY_ID_CHUNK_SIZE);
      const responses = await Promise.all(
        idChunks.map((c) =>
          fetchScheduleDayparts([`location_group_id.list=${c.join('|')}`]),
        ),
      );

      return responses.flat();
    },
    enabled: uniqueLocationGroupIds.length > 0,
  });

  const bulkReplaceAssemblies = assemblyComponents?.filter(
    (item) =>
      item.component_id === (selectedComponent && selectedComponent.id) &&
      selectedAssemblyIds.includes(item.assembly_id) &&
      !stageAssemblyData.some(
        (stageItem: { assembly_id: number }) =>
          stageItem.assembly_id === item.assembly_id,
      ),
  );

  const onHandleBulkReplace = async () => {
    if (
      selectedComponent &&
      replaceComponent &&
      selectedAssemblyIds.length > 0
    ) {
      try {
        const payload = {
          selectedComponent: selectedComponent,
          replaceComponent: replaceComponent,
          assemblyIds: selectedAssemblyIds,
        };

        const updatedIds = await bulkReplaceAssembly?.mutateAsync(payload);
        setUpdatedAssemblyComponents(updatedIds);
        handleOpenSummaryPage();
      } catch {
        Snackbar.error(lang.failedToReplace);
      }
    }
  };

  const assemblyIds = [
    ...uniq(bulkReplaceAssemblies.map(({ assembly_id }) => assembly_id)),
    ...getChildInheritedIds(
      assembliesList,
      allowedAssemblyIds,
      selectedAssemblyIds,
      selectedComponent,
    ),
  ];

  const uniqueAssemblyIds = [
    ...uniq(bulkReplaceAssemblies.map(({ assembly_id }) => assembly_id)),
  ];

  const onHandleSaveBulkReplaceStage = async () => {
    if (
      assemblyIds.length > 0 &&
      selectedComponent &&
      replaceComponent &&
      selectedAssemblyIds.length > 0
    ) {
      const payload = {
        display_name: stageBulkDisplayName,
        from_component_id: selectedComponent.id,
        to_component_id: replaceComponent.id,
        assembly_id: uniqueAssemblyIds,
        narrative: (
          narrativeGenerators[COLLECTIONS.BULK_REPLACEMENT][
            USER_ACTIONS.CREATE
          ] as (replacementName: string) => string
        )(stageBulkDisplayName),
      };

      await mutateAsync(payload);
      setUpdatedAssemblyComponents(assemblyIds);
      handleOpenSummaryPage();
      Snackbar.success(lang.stageCreationSuccess(stageBulkDisplayName));
    }
  };

  return {
    selectedAssemblyIds,
    setSelectedAssemblyIds,
    expendAssemblies,
    saveModalOpen,
    discardModalOpen,
    displaySummaryPage,
    updatedAssemblyComponents,
    setDiscardModalOpen,
    setSaveModalOpen,
    onExpendedAssemblies,
    onSelectedAssemblyIds,
    allowedAssemblyIds,
    isLoadingBulkReplaceEligibleAssemblyIdData,
    isLoadingAssemblyComponents,
    selectedComponent,
    assembliesList,
    assembliesListMapped,
    bulkReplaceAssemblies,
    isLoadingAssembly,
    onCloseSummaryPage,
    onHandleBulkReplace,
    defaultAssemblies,
    locationDayParts,
    publicationModal,
    setStageBulkReplaceNameModal,
    stageBulkReplaceNameModal,
    setStageBulkDisplayName,
    stageBulkDisplayName,
    onHandleSaveBulkReplaceStage,
    isLoading,
    stageAssemblyData,
  };
};
